import { createContext, useContext, useState, useRef, useEffect, ReactNode } from "react";
import { toast } from "sonner";

interface RadioContextType {
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  radioUrl: string | null;
  embedCode: string | null;
  isEnabled: boolean;
  isVisibleForUsers: boolean;
  isLoading: boolean;
  hasError: boolean;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setRadioConfig: (config: {
    url?: string | null;
    embedCode?: string | null;
    enabled?: boolean;
    visibleForUsers?: boolean;
  }) => void;
  stopAndReset: () => void;
}

const RadioContext = createContext<RadioContextType | undefined>(undefined);

export const useRadio = () => {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error("useRadio must be used within a RadioProvider");
  }
  return context;
};

interface RadioProviderProps {
  children: ReactNode;
}

export const RadioProvider = ({ children }: RadioProviderProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem("radio-volume");
    return saved ? parseFloat(saved) : 0.5;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [radioUrl, setRadioUrl] = useState<string | null>(null);
  const [embedCode, setEmbedCode] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isVisibleForUsers, setIsVisibleForUsers] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;
    audioRef.current.preload = "none";
    
    // Add event listeners for error handling
    const handleError = (e: Event) => {
      console.error("Radio audio error:", e);
      setHasError(true);
      setIsPlaying(false);
      setIsLoading(false);
      toast.error("Error al cargar el stream de radio. Verifica la URL.");
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setHasError(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setHasError(false);
    };

    const handlePlaying = () => {
      setIsLoading(false);
      setIsPlaying(true);
      setHasError(false);
    };

    const handleStalled = () => {
      console.warn("Radio stream stalled");
    };

    audioRef.current.addEventListener("error", handleError);
    audioRef.current.addEventListener("canplay", handleCanPlay);
    audioRef.current.addEventListener("loadstart", handleLoadStart);
    audioRef.current.addEventListener("playing", handlePlaying);
    audioRef.current.addEventListener("stalled", handleStalled);
    
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("error", handleError);
        audioRef.current.removeEventListener("canplay", handleCanPlay);
        audioRef.current.removeEventListener("loadstart", handleLoadStart);
        audioRef.current.removeEventListener("playing", handlePlaying);
        audioRef.current.removeEventListener("stalled", handleStalled);
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Update audio source when URL changes
  useEffect(() => {
    if (audioRef.current && radioUrl) {
      setHasError(false);
      audioRef.current.src = radioUrl;
      if (isPlaying) {
        audioRef.current.play().catch((err) => {
          console.error("Error playing radio:", err);
          setIsPlaying(false);
          setHasError(true);
        });
      }
    }
  }, [radioUrl]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
    localStorage.setItem("radio-volume", volume.toString());
  }, [volume, isMuted]);

  const play = () => {
    if (audioRef.current && radioUrl) {
      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setHasError(false);
        })
        .catch((err) => {
          console.error("Error playing radio:", err);
          setIsPlaying(false);
          setIsLoading(false);
          setHasError(true);
          toast.error("No se pudo reproducir el stream. Verifica la URL.");
        });
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const setVolume = (newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const setRadioConfig = (config: {
    url?: string | null;
    embedCode?: string | null;
    enabled?: boolean;
    visibleForUsers?: boolean;
  }) => {
    if (config.url !== undefined) setRadioUrl(config.url);
    if (config.embedCode !== undefined) setEmbedCode(config.embedCode);
    if (config.enabled !== undefined) setIsEnabled(config.enabled);
    if (config.visibleForUsers !== undefined) setIsVisibleForUsers(config.visibleForUsers);
    setHasError(false);
  };

  const stopAndReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsLoading(false);
  };

  return (
    <RadioContext.Provider
      value={{
        isPlaying,
        volume,
        isMuted,
        radioUrl,
        embedCode,
        isEnabled,
        isVisibleForUsers,
        isLoading,
        hasError,
        play,
        pause,
        togglePlay,
        setVolume,
        toggleMute,
        setRadioConfig,
        stopAndReset,
      }}
    >
      {children}
    </RadioContext.Provider>
  );
};
